import React, { useEffect, useState } from 'react';
import { Lightbulb, Save, AlertCircle, Zap, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './Button';
import { useSession } from '../helpers/useSessionsApi';
import styles from './AiAutonomySettings.module.css';

interface AiAutonomySettingsProps {
  sessionId: string;
}

export const AiAutonomySettings: React.FC<AiAutonomySettingsProps> = ({ sessionId }) => {
  const { data: sessionData, isLoading } = useSession(sessionId);
  const [autoApplyRecommendations, setAutoApplyRecommendations] = useState(false);
  const [fullAutonomousMode, setFullAutonomousMode] = useState(false);
  const [skipCredentialChecks, setSkipCredentialChecks] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Initialize from session data
  useEffect(() => {
    if (sessionData?.session) {
      setAutoApplyRecommendations(sessionData.session.autoApplyRecommendations ?? false);
      setFullAutonomousMode(sessionData.session.fullAutonomousMode ?? false);
      setSkipCredentialChecks(sessionData.session.skipCredentialChecks ?? false);
    }
  }, [sessionData]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await fetch('/_api/sessions/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: sessionId,
          autoApplyRecommendations,
          fullAutonomousMode,
          skipCredentialChecks,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update settings');
      }

      toast.success('AI autonomy settings saved');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className={styles.loading}>Loading...</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.setting}>
        <div className={styles.settingHeader}>
          <div className={styles.settingTitle}>
            <Zap size={18} className={styles.settingIcon} />
            <div>
              <h3 className={styles.settingName}>Full Autonomous Mode</h3>
              <p className={styles.settingDescription}>
                Execute tasks without asking for confirmation. Bot proceeds through
                each step automatically — clicks, navigates, and completes workflows end-to-end.
              </p>
            </div>
          </div>
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={fullAutonomousMode}
              onChange={(e) => setFullAutonomousMode(e.target.checked)}
              disabled={isSaving}
            />
            <span className={styles.toggleSlider} />
          </label>
        </div>

        {fullAutonomousMode && (
          <div className={styles.infoBox}>
            <AlertCircle size={16} className={styles.infoIcon} />
            <div>
              <p className={styles.infoTitle}>When enabled:</p>
              <ul className={styles.infoList}>
                <li>Tasks execute immediately without confirmation prompts</li>
                <li>Bot navigates websites, clicks buttons, fills forms autonomously</li>
                <li>Scheduled tasks run unattended — no human intervention needed</li>
                <li>Failed steps are retried with alternative approaches</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      <div className={styles.setting}>
        <div className={styles.settingHeader}>
          <div className={styles.settingTitle}>
            <Globe size={18} className={styles.settingIcon} />
            <div>
              <h3 className={styles.settingName}>Skip Credential Pre-checks</h3>
              <p className={styles.settingDescription}>
                Don't block tasks when inferred credentials are missing. Let the bot proceed and
                handle missing integrations at runtime.
              </p>
            </div>
          </div>
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={skipCredentialChecks}
              onChange={(e) => setSkipCredentialChecks(e.target.checked)}
              disabled={isSaving}
            />
            <span className={styles.toggleSlider} />
          </label>
        </div>
      </div>

      <div className={styles.setting}>
        <div className={styles.settingHeader}>
          <div className={styles.settingTitle}>
            <Lightbulb size={18} className={styles.settingIcon} />
            <div>
              <h3 className={styles.settingName}>Auto-Apply Recommendations</h3>
              <p className={styles.settingDescription}>
                Automatically implement high-priority recommendations after code execution
              </p>
            </div>
          </div>
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={autoApplyRecommendations}
              onChange={(e) => setAutoApplyRecommendations(e.target.checked)}
              disabled={isSaving}
            />
            <span className={styles.toggleSlider} />
          </label>
        </div>
      </div>

      <div className={styles.actions}>
        <Button onClick={handleSave} disabled={isSaving} className={styles.saveButton}>
          <Save size={16} />
          {isSaving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};
